'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import HouseAd from './HouseAd';
import { useAdFallback } from './useAdFallback';

const ADSENSE_CLIENT_ID = 'ca-pub-2214184889025947';

/**
 * Sticky vertical ad that lives INSIDE an existing right-hand sidebar column
 * (e.g. the KB article TOC column). Unlike <SkyscraperAd/>, this is in-flow —
 * it occupies the sidebar grid track rather than being pinned to the viewport
 * gutter — so it works on the wide max-w-7xl article+sidebar layouts where a
 * viewport-fixed unit would overlap content. `sticky top-24` keeps it in view as
 * the reader scrolls. Desktop only (`hidden lg:block`); the sidebar itself is
 * hidden on mobile.
 *
 * SETUP: shares VERTICAL_SLOT with the skyscraper units (one vertical slot,
 * reused everywhere). Renders/pushes nothing until a real numeric slot is set.
 * Requires the adsbygoogle.js loader (<AdSenseLoader />) on the same page.
 *
 * HOUSE-AD FALLBACK: when useAdFallback determines AdSense declined this slot,
 * one of our own SaaS products is rendered instead. AdSense always gets first
 * refusal — the check runs on a delay and resolves ambiguous states to
 * "filled", so a paying impression is never displaced. See lib/house-ads.ts.
 */
const VERTICAL_SLOT = '6902116546';

export default function SidebarAd() {
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement | null>(null);
  const pathname = usePathname();
  const { unfilled, blocked } = useAdFallback(insRef, { slot: VERTICAL_SLOT, placement: 'sidebar' });
  const isConfigured = /^\d+$/.test(VERTICAL_SLOT);

  useEffect(() => {
    if (!isConfigured || pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      // Loader not ready yet or slot already filled — safe to ignore.
    }
  }, [isConfigured]);

  if (!isConfigured) return null;

  return (
    <div aria-label="Advertisement" className="hidden lg:block sticky top-24">
      {!unfilled && (
        <div className="mb-1 select-none text-center text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Advertisement
        </div>
      )}
        {/* Unmount the <ins> once it is known unfilled rather than just hiding
            it. Ad blockers apply cosmetic filters as well as network blocks,
            and a :has(> ins.adsbygoogle) rule would hide this whole container —
            taking the house ad with it. Removing the element means such a rule
            has nothing to match. Detection has already completed by this point
            (useAdFallback settles once), so the ref is no longer needed. */}
      {!unfilled && (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', minHeight: 600 }}
          data-ad-client={ADSENSE_CLIENT_ID}
          data-ad-slot={VERTICAL_SLOT}
          data-ad-format="vertical"
          data-full-width-responsive="true"
        />
      )}
      {unfilled && <HouseAd pathname={pathname} blocked={blocked} />}
    </div>
  );
}
